import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { useEvent, useUser, useAuth } from '../store';
import { createParticipant, saveEvent, saveCurrentUser } from '../utils';
import { addToPublicEvents, addEventToUser, loadTemplate, saveTemplate } from '../services/firestore';
import type { Event } from '../types';

/**
 * Create Event Page
 * Admin page for creating a new fight night event
 */
export function CreateEventPage() {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('template');
  
  const [eventName, setEventName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!!templateId);
  const [templateData, setTemplateData] = useState<Event | null>(null);
  const { createEvent, addParticipant } = useEvent();
  const { setCurrentParticipant, setIsAdmin, setEventId } = useUser();
  const { currentUser, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Require authentication - redirect to landing if not authenticated
  useEffect(() => {
    // Wait for auth to finish loading
    if (!authLoading && (!isAuthenticated || !currentUser)) {
      navigate('/');
      return;
    }
    
    // ALL users MUST select a template (including ultra-admins for events)
    if (!authLoading && isAuthenticated && currentUser && !templateId) {
      navigate('/create/select-template');
      return;
    }
  }, [isAuthenticated, currentUser, authLoading, templateId, navigate]);

  // Load template data
  useEffect(() => {
    if (templateId && currentUser) {
      const loadTemplateData = async () => {
        setLoading(true);
        try {
          const templateEvent = await loadTemplate(templateId);
          if (templateEvent) {
            setTemplateData(templateEvent);
            // Pre-fill event name from template (user can customize)
            setEventName(`${templateEvent.name} - ${currentUser.username}'s Pool`);
          } else {
            setError('Template not found');
          }
        } catch (err) {
          console.error('Error loading template:', err);
          setError('Failed to load template');
        } finally {
          setLoading(false);
        }
      };
      loadTemplateData();
    }
  }, [templateId, currentUser]);

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-secondary-text">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated
  if (!isAuthenticated || !currentUser) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-secondary-text">Loading template...</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Validate inputs
    if (!eventName.trim()) {
      setError('Event name is required');
      return;
    }

    if (!currentUser) {
      setError('You must be logged in to create an event');
      return;
    }

    try {
      // Validate template is provided
      if (!templateId || !templateData) {
        setError('Template is required to create an event');
        return;
      }

      // Create event instance (references template, no fights copied)
      const adminParticipantId = `participant-${Date.now()}`;
      const event = createEvent(eventName.trim(), adminParticipantId);
      
      // Set admin username and link to template
      event.adminUsername = currentUser.username;
      event.templateId = templateId;
      event.sport = templateData.sport;
      event.fights = []; // Event instances don't store fights - they reference template
      
      // Create admin participant with user's username
      const adminParticipant = createParticipant(adminParticipantId, currentUser.username, event.id);
      
      // Add admin as first participant
      addParticipant(adminParticipant);
      
      // Update event with participant
      event.participants = [adminParticipant];
      
      // Set public/private status - create final event AFTER all updates
      const finalEvent = { ...event, isPublic } as Event;
      
      // Link event to user
      await addEventToUser(currentUser.id, finalEvent.id);
      
      // Update template's usage count
      if (templateData) {
        templateData.templateUsageCount = (templateData.templateUsageCount || 0) + 1;
        await saveTemplate(templateData);
      }
      
      // Save event to storage
      await saveEvent(finalEvent);
      
      // If public, add to public events list
      if (isPublic) {
        await addToPublicEvents(finalEvent.id);
      }
      
      // Save current participant for this event (session-specific)
      saveCurrentUser(finalEvent.id, adminParticipant.id);
      
      // Set user context
      setCurrentParticipant(adminParticipant);
      setIsAdmin(true);
      setEventId(finalEvent.id);
      
      console.log('✅ Event instance created successfully:', finalEvent.id);
      console.log('Event references template:', finalEvent.templateId);
      console.log('Event has', finalEvent.participants.length, 'participants');
      
      // Navigate to admin panel - creator should manage their event
      navigate(`/admin/${finalEvent.id}`);
    } catch (err) {
      setError('Failed to create event. Please try again.');
      console.error('Error creating event:', err);
    }
  };

  return (
    <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-text mb-2">
            {templateId ? 'Create Event from Template' : 'Create Event'}
          </h1>
          <p className="text-secondary-text text-lg">
            {templateId 
              ? 'Review and customize your event details'
              : 'Set up your fight night betting pool'}
          </p>
          {templateId && (
            <p className="text-sm text-muted-text mt-2">
              Fights and odds will be copied from the template
            </p>
          )}
        </div>

        <Card padding="lg">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Event Name"
              placeholder="e.g., UFC 300 Fight Night"
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              error={error}
              helperText="Give your event a memorable name"
              autoFocus
            />

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isPublic"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="w-5 h-5 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="isPublic" className="text-primary-text">
                Make this event public
                <span className="block text-sm text-secondary-text">
                  Public events appear in "Available Events" for others to join
                </span>
              </label>
            </div>

            <Button type="submit" variant="primary" fullWidth>
              Create Event
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={() => navigate('/games')}
            >
              Back to Games
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

